
import React, { useState } from 'react';
import { Domain } from '@/types/partner';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface DomainListProps {
  domains: Domain[];
  onSelectDomain: (domainId: string) => void;
}

const DomainList = ({ domains, onSelectDomain }: DomainListProps) => {
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());
  
  const toggleDomain = (domainId: string) => {
    const newExpanded = new Set(expandedDomains);
    if (newExpanded.has(domainId)) {
      newExpanded.delete(domainId);
    } else {
      newExpanded.add(domainId);
    }
    setExpandedDomains(newExpanded);
  };
  
  const renderDomain = (domain: Domain) => {
    const hasSubdomains = domain.subdomains && domain.subdomains.length > 0;
    const isExpanded = expandedDomains.has(domain.id);
    
    return (
      <div key={domain.id} className="space-y-2">
        <Card className="overflow-hidden">
          <div 
            className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50"
            onClick={() => {
              if (hasSubdomains) {
                toggleDomain(domain.id);
              } else {
                onSelectDomain(domain.id);
              }
            }}
          >
            {hasSubdomains ? (
              isExpanded ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              )
            ) : (
              <div className="w-5" />
            )}
            
            <div className="flex items-center gap-3 flex-1">
              <div className="w-12 h-12 rounded-md bg-muted flex items-center justify-center overflow-hidden">
                {domain.image ? (
                  <img 
                    src={domain.image} 
                    alt={domain.name} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-lg font-semibold text-muted-foreground">
                    {domain.name.charAt(0)}
                  </span>
                )}
              </div>
              <span className="font-medium">{domain.name}</span>
            </div>
          </div>
        </Card>
        
        {isExpanded && hasSubdomains && (
          <div className="pl-6 space-y-2">
            {domain.subdomains?.map(subdomain => renderDomain(subdomain))}
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="space-y-2">
      {domains.map(renderDomain)}
    </div>
  );
};

export default DomainList;
